import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { toast } from 'sonner';
import {
  Trophy, RefreshCw, ChevronDown, ChevronRight, Target,
  TrendingUp, TrendingDown, Lightbulb, Heart, Users,
  Loader2, Award, Zap, BarChart3, Calendar, Sparkles, Rocket, Plus, Check
} from 'lucide-react';
import api from '../api';

export default function AICoachingPanel() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [coaching, setCoaching] = useState(null);
  const [createdTasks, setCreatedTasks] = useState(new Set()); // Track which items are already tasks
  const [creatingTask, setCreatingTask] = useState(null); // Currently creating task
  const [expandedSections, setExpandedSections] = useState({
    wins: true,
    growth: true,
    recommendations: true,
    opportunities: true,
    performance: false,
  });

  const fetchCoaching = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await api.get('/leadership-ai/coaching');
      setCoaching(response.data);
    } catch (error) {
      console.error('Failed to fetch AI coaching:', error);
      toast.error('Failed to load AI coaching');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCoaching();
  }, []);

  const handleRefresh = () => {
    fetchCoaching(true);
    setCreatedTasks(new Set()); // Reset created tasks on refresh
  };
  
  const handleCreateTask = async (text, sourceType, index) => {
    const taskKey = `${sourceType}_${index}`;
    setCreatingTask(taskKey);
    
    try {
      // Truncate title if too long
      const title = text.length > 100 ? text.substring(0, 97) + '...' : text;
      
      await api.post('/leadership-ai/coaching/create-task', null, {
        params: {
          task_title: title,
          task_description: text,
          priority: sourceType === 'next_step' ? 'high' : 'medium',
          source_type: sourceType
        }
      });
      
      setCreatedTasks(prev => new Set([...prev, taskKey]));
      toast.success('✓ Task created from AI Coach suggestion');
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(null);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return null;
  };

  const getTrendColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  if (loading) {
    return (
      <Card className="border-amber-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-32" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!coaching) {
    return (
      <Card className="border-amber-200">
        <CardContent className="py-8 text-center">
          <Trophy className="h-12 w-12 mx-auto mb-3 text-amber-300" />
          <p className="text-muted-foreground">Unable to load AI coaching</p>
          <Button onClick={handleRefresh} variant="outline" className="mt-3">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { coaching: coachingData, performance_data, user_name } = coaching;

  return (
    <Card className="border-amber-200" data-testid="ai-coaching-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                AI Coach
                <Badge variant="outline" className="bg-amber-100 text-amber-700">
                  Personal
                </Badge>
              </CardTitle>
              <CardDescription>
                Your performance insights & growth recommendations
              </CardDescription>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Motivation Message */}
        {coachingData.motivation && (
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-lg border border-amber-200">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-amber-600 mt-0.5" />
              <p className="text-sm italic">{coachingData.motivation}</p>
            </div>
          </div>
        )}

        {/* Wins */}
        <Collapsible open={expandedSections.wins} onOpenChange={() => toggleSection('wins')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-950/30 transition-colors">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-green-600" />
                <span className="font-medium">Wins & Achievements</span>
                <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                  {coachingData.wins?.length || 0}
                </Badge>
              </div>
              {expandedSections.wins ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              {coachingData.wins?.map((win, idx) => (
                <div key={idx} className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border-l-4 border-green-400">
                  <div className="flex items-start gap-2">
                    <Trophy className="h-4 w-4 text-green-600 mt-0.5" />
                    <p className="text-sm">{win}</p>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Growth Areas */}
        <Collapsible open={expandedSections.growth} onOpenChange={() => toggleSection('growth')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-950/30 transition-colors">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                <span className="font-medium">Growth Opportunities</span>
                <Badge variant="outline" className="bg-blue-100 text-blue-700 text-xs">
                  {coachingData.growth_areas?.length || 0}
                </Badge>
              </div>
              {expandedSections.growth ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              {coachingData.growth_areas?.map((area, idx) => (
                <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border-l-4 border-blue-400">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm flex-1">{area}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-blue-600 hover:bg-blue-100"
                      onClick={() => handleCreateTask(area, 'growth_area', idx)}
                      disabled={creatingTask === `growth_area_${idx}` || createdTasks.has(`growth_area_${idx}`)}
                    >
                      {createdTasks.has(`growth_area_${idx}`) ? (
                        <><Check className="h-3 w-3 mr-1" /> Added</>
                      ) : creatingTask === `growth_area_${idx}` ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Plus className="h-3 w-3 mr-1" /> Task</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Recommendations */}
        <Collapsible open={expandedSections.recommendations} onOpenChange={() => toggleSection('recommendations')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-950/30 transition-colors">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-purple-600" />
                <span className="font-medium">This Week&apos;s Actions</span>
                <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs">
                  {coachingData.recommendations?.length || 0}
                </Badge>
              </div>
              {expandedSections.recommendations ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              {coachingData.recommendations?.map((rec, idx) => (
                <div key={idx} className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border-l-4 border-purple-400">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 flex-1">
                      <Zap className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                      <p className="text-sm">{rec}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-purple-600 hover:bg-purple-100 shrink-0"
                      onClick={() => handleCreateTask(rec, 'recommendation', idx)}
                      disabled={creatingTask === `recommendation_${idx}` || createdTasks.has(`recommendation_${idx}`)}
                    >
                      {createdTasks.has(`recommendation_${idx}`) ? (
                        <><Check className="h-3 w-3 mr-1" /> Added</>
                      ) : creatingTask === `recommendation_${idx}` ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Plus className="h-3 w-3 mr-1" /> Task</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Opportunities */}
        {coachingData.opportunities && coachingData.opportunities.length > 0 && (
          <Collapsible open={expandedSections.opportunities} onOpenChange={() => toggleSection('opportunities')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 rounded-lg hover:from-emerald-100 hover:to-teal-100 transition-colors">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  <span className="font-medium">Opportunities to Explore</span>
                  <Badge variant="outline" className="bg-emerald-100 text-emerald-700 text-xs">
                    {coachingData.opportunities?.length || 0}
                  </Badge>
                </div>
                {expandedSections.opportunities ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-2">
                {coachingData.opportunities?.map((opp, idx) => (
                  <div key={idx} className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 rounded-lg border-l-4 border-emerald-400">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1">
                        <Sparkles className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        <p className="text-sm">{opp}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-emerald-600 hover:bg-emerald-100 shrink-0"
                        onClick={() => handleCreateTask(opp, 'opportunity', idx)}
                        disabled={creatingTask === `opportunity_${idx}` || createdTasks.has(`opportunity_${idx}`)}
                      >
                        {createdTasks.has(`opportunity_${idx}`) ? (
                          <><Check className="h-3 w-3 mr-1" /> Added</>
                        ) : creatingTask === `opportunity_${idx}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <><Plus className="h-3 w-3 mr-1" /> Task</>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Next Steps - Always show prominently */}
        {coachingData.next_steps && coachingData.next_steps.length > 0 && (
          <div className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-950/30 dark:to-orange-950/30 rounded-lg border-2 border-amber-300">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-amber-700" />
                <span className="font-semibold text-amber-800">Your Next Steps</span>
                <Badge className="bg-amber-500 text-white text-xs">Action Items</Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                onClick={() => {
                  coachingData.next_steps.forEach((step, idx) => {
                    if (!createdTasks.has(`next_step_${idx}`)) {
                      handleCreateTask(step, 'next_step', idx);
                    }
                  });
                }}
                disabled={creatingTask !== null}
              >
                <Plus className="h-3 w-3 mr-1" /> Add All as Tasks
              </Button>
            </div>
            <div className="space-y-2">
              {coachingData.next_steps?.map((step, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 bg-white/60 rounded-lg group">
                  <div className="h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {createdTasks.has(`next_step_${idx}`) ? <Check className="h-3 w-3" /> : idx + 1}
                  </div>
                  <p className={`text-sm text-amber-900 flex-1 ${createdTasks.has(`next_step_${idx}`) ? 'line-through opacity-60' : ''}`}>{step}</p>
                  {!createdTasks.has(`next_step_${idx}`) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-amber-700 hover:bg-amber-200 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={() => handleCreateTask(step, 'next_step', idx)}
                      disabled={creatingTask === `next_step_${idx}`}
                    >
                      {creatingTask === `next_step_${idx}` ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Insights (for managers) */}
        {coachingData.team_insights && coachingData.team_insights.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
              <Users className="h-5 w-5 text-indigo-600" />
              <span className="font-medium">Team Insights</span>
            </div>
            <div className="space-y-2 pl-4">
              {coachingData.team_insights.map((insight, idx) => (
                <div key={idx} className="p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border-l-4 border-indigo-400">
                  <p className="text-sm">{insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Snapshot */}
        <Collapsible open={expandedSections.performance} onOpenChange={() => toggleSection('performance')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-slate-600" />
                <span className="font-medium">Performance Data</span>
              </div>
              {expandedSections.performance ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {/* Tasks */}
              {performance_data?.tasks && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Tasks (30d)</span>
                    {getTrendIcon(performance_data.tasks.change_percent)}
                  </div>
                  <p className="text-xl font-bold">{performance_data.tasks.completed_last_30d}</p>
                  <p className={`text-xs ${getTrendColor(performance_data.tasks.change_percent)}`}>
                    {performance_data.tasks.change_percent > 0 ? '+' : ''}{performance_data.tasks.change_percent}% vs prev
                  </p>
                </div>
              )}
              
              {/* Sales */}
              {performance_data?.sales && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Deals Won (30d)</span>
                    {getTrendIcon(performance_data.sales.change_percent)}
                  </div>
                  <p className="text-xl font-bold">{performance_data.sales.deals_won_30d}</p>
                  <p className={`text-xs ${getTrendColor(performance_data.sales.change_percent)}`}>
                    Win rate: {performance_data.sales.win_rate}%
                  </p>
                </div>
              )}
              
              {/* Support */}
              {performance_data?.support && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Tickets (30d)</span>
                    {getTrendIcon(performance_data.support.change_percent)}
                  </div>
                  <p className="text-xl font-bold">{performance_data.support.tickets_resolved_30d}</p>
                  <p className={`text-xs ${getTrendColor(performance_data.support.change_percent)}`}>
                    {performance_data.support.change_percent > 0 ? '+' : ''}{performance_data.support.change_percent}% vs prev
                  </p>
                </div>
              )}
              
              {/* AI Usage */}
              {performance_data?.ai_usage && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">AI Tasks</span>
                  </div>
                  <p className="text-xl font-bold">{performance_data.ai_usage.tasks_created_30d}</p>
                  <p className="text-xs text-muted-foreground">
                    {performance_data.ai_usage.tasks_completed_30d} completed
                  </p>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
